import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CUSTOMER_KEY,
  type Customer,
  invalidateCustomerData,
} from '@hungryroot/customer';
import { StatusCode } from '@hungryroot/rest';
import { postUpdateSpicyLevel } from '@hungryroot/rest/api/update_spicy_level/post';
import { type SpicyLevel } from '../../../components/SpicyButtons/spice-level';

interface PostUpdateSpicyLevelParams {
  spicy_level: SpicyLevel | null;
}

export function usePersistSpicePreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { data: PostUpdateSpicyLevelParams }) => {
      const { data } = params;
      const res = await postUpdateSpicyLevel(data);

      if (res.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(res.data, null, 2));
      }
      return res.data;
    },
    onMutate: async (newValue) => {
      await queryClient.cancelQueries({
        queryKey: [CUSTOMER_KEY],
      });
      const existingCustomer = queryClient.getQueryData<{
        data: Customer;
      }>(CUSTOMER_KEY);

      if (!existingCustomer?.data) {
        return { existingCustomer, newValue };
      }

      queryClient.setQueryData(CUSTOMER_KEY, {
        ...existingCustomer,
        data: {
          ...existingCustomer.data,
          spicy_level: newValue.data.spicy_level,
        },
      });

      return { existingCustomer, newValue };
    },
    onError: (
      err,
      newValue,
      context:
        | {
            existingCustomer: unknown;
          }
        | undefined
    ) => {
      queryClient.setQueryData([CUSTOMER_KEY], context?.existingCustomer);
    },
    onSuccess: () => {
      invalidateCustomerData(queryClient);
    },
  });
}
